import { createSlice } from "@reduxjs/toolkit";
import {
  fetchNewsThunk,
  fetchArticleThunk,
  fetchByTickerThunk,
  submitArticleThunk,
  fetchModerationQueueThunk,
  decideModerationThunk,
  fetchFollowedJournalistsThunk,
} from "./newsActionThunks";

// Every thunk below gets a complete pending/fulfilled/rejected triplet
// deliberately -- this session found and fixed two other slices
// (postEvidenceSlice.js, monetizationSlice.js) where a "just add fulfilled"
// shortcut left failures silent. Not repeating that here.
const initialState = {
  feed: [],
  feedLoading: false,
  feedError: null,

  byTicker: {},        // { [symbol]: articles[] }
  byTickerLoading: false,
  byTickerError: null,

  articleById: {},      // { [id]: article }
  articleLoading: false,
  articleError: null,

  submitStatus: null,   // "success" | "error" | null
  submitError: null,
  lastSubmitted: null,

  moderationQueue: [],
  moderationLoading: false,
  moderationError: null,

  decisionStatus: null, // "success" | "error" | null
  decisionError: null,

  followedJournalists: [],
  followedJournalistsLoading: false,
  followedJournalistsError: null,
};

const newsSlice = createSlice({
  name: "news",
  initialState,
  reducers: {
    resetSubmitStatus: (state) => {
      state.submitStatus = null;
      state.submitError = null;
    },
    resetDecisionStatus: (state) => {
      state.decisionStatus = null;
      state.decisionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewsThunk.pending, (state) => {
        state.feedLoading = true;
        state.feedError = null;
      })
      .addCase(fetchNewsThunk.fulfilled, (state, { payload }) => {
        state.feedLoading = false;
        state.feed = Array.isArray(payload.data) ? payload.data : [];
      })
      .addCase(fetchNewsThunk.rejected, (state, { payload }) => {
        state.feedLoading = false;
        state.feedError = payload;
      });

    builder
      .addCase(fetchByTickerThunk.pending, (state) => {
        state.byTickerLoading = true;
        state.byTickerError = null;
      })
      .addCase(fetchByTickerThunk.fulfilled, (state, { payload }) => {
        state.byTickerLoading = false;
        state.byTicker[payload.symbol] = Array.isArray(payload.data) ? payload.data : [];
      })
      .addCase(fetchByTickerThunk.rejected, (state, { payload }) => {
        state.byTickerLoading = false;
        state.byTickerError = payload;
      });

    builder
      .addCase(fetchArticleThunk.pending, (state) => {
        state.articleLoading = true;
        state.articleError = null;
      })
      .addCase(fetchArticleThunk.fulfilled, (state, { payload }) => {
        state.articleLoading = false;
        state.articleById[payload.id] = payload;
      })
      .addCase(fetchArticleThunk.rejected, (state, { payload }) => {
        state.articleLoading = false;
        state.articleError = payload;
      });

    builder
      .addCase(submitArticleThunk.pending, (state) => {
        state.submitStatus = null;
        state.submitError = null;
      })
      .addCase(submitArticleThunk.fulfilled, (state, { payload }) => {
        state.submitStatus = "success";
        state.lastSubmitted = payload;
      })
      .addCase(submitArticleThunk.rejected, (state, { payload }) => {
        state.submitStatus = "error";
        state.submitError = payload;
      });

    builder
      .addCase(fetchModerationQueueThunk.pending, (state) => {
        state.moderationLoading = true;
        state.moderationError = null;
      })
      .addCase(fetchModerationQueueThunk.fulfilled, (state, { payload }) => {
        state.moderationLoading = false;
        state.moderationQueue = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchModerationQueueThunk.rejected, (state, { payload }) => {
        state.moderationLoading = false;
        state.moderationError = payload;
      });

    builder
      .addCase(decideModerationThunk.pending, (state) => {
        state.decisionStatus = null;
        state.decisionError = null;
      })
      .addCase(decideModerationThunk.fulfilled, (state, { payload }) => {
        state.decisionStatus = "success";
        state.moderationQueue = state.moderationQueue.filter((a) => a.id !== payload.id);
      })
      .addCase(decideModerationThunk.rejected, (state, { payload }) => {
        state.decisionStatus = "error";
        state.decisionError = payload;
      });

    builder
      .addCase(fetchFollowedJournalistsThunk.pending, (state) => {
        state.followedJournalistsLoading = true;
        state.followedJournalistsError = null;
      })
      .addCase(fetchFollowedJournalistsThunk.fulfilled, (state, { payload }) => {
        state.followedJournalistsLoading = false;
        state.followedJournalists = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchFollowedJournalistsThunk.rejected, (state, { payload }) => {
        state.followedJournalistsLoading = false;
        state.followedJournalistsError = payload;
      });
  },
});

export const { resetSubmitStatus, resetDecisionStatus } = newsSlice.actions;
export default newsSlice.reducer;

export const selectNewsFeed = (state) => state.news?.feed || [];
export const selectNewsByTicker = (state, symbol) => state.news?.byTicker?.[symbol] || [];
export const selectModerationQueue = (state) => state.news?.moderationQueue || [];
export const selectFollowedJournalists = (state) => state.news?.followedJournalists || [];
